import { useTranslation } from "react-i18next";
import { VictoryAxis, VictoryBar, VictoryChart, VictoryTooltip } from "victory";

import { useStepsChartConfig } from "~/hooks/Steps";
import { formatInteger } from "~/utils";

interface StepsChartProps {
  selectedRange: string;
  navigationOffset: number;
  selectedDate?: Date;
  chartData: { x: Date | number; y: number }[];
  chartConfig?: {
    scale: { x: "time" | "linear" };
    domain: { x: [Date, Date] | [number, number]; y: [number, number] };
    tickValues: (Date | number)[];
    tickFormat: (x: Date | number) => string;
    yTickValues?: number[];
  };
}

export const StepsChart = ({
  selectedRange,
  navigationOffset,
  selectedDate,
  chartData,
  chartConfig: parentChartConfig,
}: StepsChartProps) => {
  useTranslation();

  // Transform API data to chart format
  const transformedChartData = chartData.map((d) => ({ x: d.x, y: d.y }));

  // Always call the hook to maintain React rules
  const internalChartConfig = useStepsChartConfig({
    selectedRange,
    navigationOffset,
    selectedDate,
    lowChartData: transformedChartData,
  });

  // Use parent chart config if provided, otherwise use internal
  const chartConfig = parentChartConfig ?? internalChartConfig;

  // Use dynamic Y-axis scale from chart config
  const yAxisScale = {
    min: chartConfig.domain.y[0],
    max: chartConfig.domain.y[1],
  };

  // Custom Y-axis formatter based on time range
  const formatYAxis = (value: number) => {
    if (selectedRange === "6m" || selectedRange === "1y") {
      // For monthly data, show in thousands
      if (value >= 100000) {
        return `${formatInteger(value / 1000)}k`;
      }
      return formatInteger(value);
    } else if (selectedRange === "1d") {
      // For hourly data, show exact numbers
      return formatInteger(value);
    } else {
      // For daily data, show in thousands if over 10k
      if (value >= 10000) {
        return `${(value / 1000).toFixed(1)}k`;
      }
      return formatInteger(value);
    }
  };

  return (
    <div className="h-64 w-full">
      <VictoryChart
        domain={{ ...chartConfig.domain, y: [yAxisScale.min, yAxisScale.max] }}
        padding={{ top: 30, bottom: 50, left: 60, right: 40 }}
        width={600}
        height={250}
      >
        <VictoryAxis
          dependentAxis
          tickValues={chartConfig.yTickValues}
          tickFormat={formatYAxis}
          style={{
            axis: { stroke: "#374151" },
            grid: { stroke: "#374151", strokeDasharray: "3,3", opacity: 0.3 },
            tickLabels: { fill: "#9CA3AF", fontSize: 12 },
          }}
        />
        <VictoryAxis
          tickFormat={chartConfig.tickFormat}
          tickValues={chartConfig.tickValues}
          style={{
            axis: { stroke: "#374151" },
            grid: { stroke: "transparent", strokeWidth: 0 },
            tickLabels: { fill: "#9CA3AF", fontSize: 12 },
          }}
        />

        {/* Total Steps Bars */}
        <VictoryBar
          data={transformedChartData}
          x="x"
          y="y"
          style={{
            data: {
              fill: "#8B5CF6",
              opacity: 0.9,
              width: 8,
            },
          }}
          labelComponent={
            <VictoryTooltip
              style={{ fill: "#F9FAFB" }}
              flyoutStyle={{
                stroke: "#374151",
                strokeWidth: 1,
                fill: "#1F2937",
              }}
            />
          }
        />
      </VictoryChart>
    </div>
  );
};
